import React, { useRef } from "react";
import { Box, CircularProgress, Table, Typography, useTheme } from "@mui/material";
import InfiniteScroll from "react-infinite-scroll-component";
import {
  BaseSensorModel,
  LimitOffsetPageBaseSensorModel,
} from "@veris-health/med-data-ms/lib/v1/api";
import dayjs from "dayjs";
import { VrsSortedTableHeader } from "../../../ui/components/VrsSortedTableHeader";
import {
  StyledTableBody,
  StyledTableCell,
  StyledTableRow,
} from "../../../ui/components/Tables/shared/StyledTableElements";
import { Status } from "../../shared/interfaces";
import { columns, MioHistoryColumn, TableColumnNames } from "../config/mioHistory";
import { Sorting } from "../useMioDataPoints";

export interface MioTableProps {
  data: LimitOffsetPageBaseSensorModel;
  status?: Status;
  total?: number;
  error?: string;
  fetchMore: () => void;
  onSort?: (filter: string) => void;
  sorting?: Sorting;
}

export const MioHistoryTable = React.forwardRef<HTMLTableElement, MioTableProps>(
  ({ data, status, fetchMore, onSort, sorting, error }: MioTableProps, ref) => {
    const scrollParentRef = useRef<HTMLElement>(null);

    if (error) {
      return (
        <Typography variant="subtitle2" sx={{ marginTop: "0.5rem" }}>
          {error}
        </Typography>
      );
    }

    return (
      <>
        {data.items.length > 0 ? (
          <Box
            id="users-table-scroll-container"
            ref={scrollParentRef}
            sx={{
              backgroundColor: (theme) => theme.veris.colors.neutrals.white,
              border: (theme) => `1px solid ${theme.veris.colors.neutrals["grey-light"]}`,
              borderRadius: (theme) => theme.shape.borderRadius,
            }}
          >
            <InfiniteScroll
              scrollableTarget="users-table-scroll-container"
              hasMore={data.total - data.items.length > 0}
              style={{ borderRadius: 15, maxHeight: "73vh" }}
              height="73vh"
              dataLength={data?.items?.length}
              next={() => {
                fetchMore();
              }}
              loader={
                <Box
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                  p={1}
                  borderTop={(theme) => `1px solid ${theme.veris.colors.neutrals["grey-2"]}`}
                >
                  <CircularProgress />
                </Box>
              }
            >
              <Table ref={ref}>
                <VrsSortedTableHeader
                  columns={columns}
                  setOrderBy={onSort}
                  order={sorting?.direction}
                  orderBy={sorting?.key}
                />
                <StyledTableBody>
                  {data.items.map((measurement) => (
                    <StyledTableRow key={`${measurement.id}-${measurement.user_id}`}>
                      {columns.map((column) => (
                        <MioDataColumn column={column} data={measurement} key={column.id} />
                      ))}
                    </StyledTableRow>
                  ))}
                </StyledTableBody>
              </Table>
            </InfiniteScroll>
          </Box>
        ) : (
          <>
            {status === "idle" && data.items.length === 0 && (
              <Typography
                color={({ veris }) => veris.colors.neutrals["grey-3"]}
                textAlign="center"
                variant="body"
              >
                There is no data at this moment.
              </Typography>
            )}
          </>
        )}
      </>
    );
  },
);

function MioDataColumn({ column, data }: { column: MioHistoryColumn; data: BaseSensorModel }) {
  const theme = useTheme();
  return (
    <StyledTableCell key={column.id}>
      {column.id === TableColumnNames.Id && (
        <Typography variant="subtitle2" color={theme.veris.colors.neutrals["grey-4"]}>
          {data.id}
        </Typography>
      )}
      {column.id === TableColumnNames.UserId && (
        <Typography variant="subtitle2" color={theme.veris.colors.neutrals["grey-4"]}>
          {data.user_id}
        </Typography>
      )}
      {column.id === TableColumnNames.TimeStamp && (
        <Typography variant="subtitle2" color={theme.veris.colors.neutrals["grey-4"]}>
          {data.timestamp ? dayjs(data.timestamp).format() : "N/A"}
        </Typography>
      )}
      {column.id === TableColumnNames.IngestionTimestamp && (
        <Typography variant="subtitle2" color={theme.veris.colors.neutrals["grey-4"]}>
          {data.ingestion_timestamp ? dayjs(data.ingestion_timestamp).format() : "N/A"}
        </Typography>
      )}
      {column.id === TableColumnNames.MioTimestamp && (
        <Typography variant="subtitle2" color={theme.veris.colors.neutrals["grey-4"]}>
          {data.mio_data_timestamp ? dayjs(data.mio_data_timestamp).format() : "N/A"}
        </Typography>
      )}
      {column.id === TableColumnNames.CreatedAt && (
        <Typography variant="subtitle2" color={theme.veris.colors.neutrals["grey-4"]}>
          {data.mio_created_at ? dayjs(data.mio_created_at).format() : "N/A"}
        </Typography>
      )}
    </StyledTableCell>
  );
}
